import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import AppForm from "../../../../components/form/Form";
import { useAppForm } from "../../../../components/form/useForm";
import useToaster from "../../../../app/hooks/useToaster";
import agent from "../../../../app/api/agent";
import { userActions } from "../../../user/actions";
import { useAppDispatch } from "../../../../app/hooks/useAppDispatch";
import { StudentProfileVirtualAgreementModel } from "../../../students/types";
import { Button, Popup } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { NonAuthRoutes } from "../../../../app/auth/routes";
import ShIcon from "../../../../components/ShIcon";

interface Props {
  studentId: number;
  onSaving: (isSaving: boolean) => void;
}

const StudentProfileVirtualAgreementForm = ({ studentId, onSaving }: Props) => {
  const { t } = useTranslation();
  const formHook = useAppForm<StudentProfileVirtualAgreementModel & { privacyPolicy: boolean }>({
    defaultValues: {
      parentName: "",
      parentSurname: "",
      parentPhone: "",
      allowPhotos: false,
      isOver18: undefined,
      privacyPolicy: false,
    },
  });
  const { success, apiError } = useToaster();
  const isOver18 = formHook.watch("isOver18");
  const [isSaving, setSaving] = useState(false);
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(
    (data: StudentProfileVirtualAgreementModel) => {
      setSaving(true);
      onSaving(true);

      const payload: StudentProfileVirtualAgreementModel = {
        isOver18: data.isOver18,
        allowPhotos: data.allowPhotos,
        allowTravel: data.allowTravel,
      };

      if (data.isOver18 === false) {
        payload.parentName = data.parentName;
        payload.parentSurname = data.parentSurname;
        payload.parentPhone = data.parentPhone;
      }

      agent.Students.initialiseVirtualAgreement(studentId, payload)
        .then((url) => {
          window.open(url, "_blank", "noopener,noreferrer");
          success(
            <>
              {t("my_profile.student.agreement.virtual_agreement_initialized_message")}{" "}
              <a href={url} target="_blank" rel="noopener noreferrer">
                {t("my_profile.student.agreement.virtual_agreement_initialized_link")}
              </a>
            </>
          );
          dispatch(
            userActions.updateUser({
              agreement: {
                status: "virtualsignpending",
                signUrl: url,
              },
            })
          );
        })
        .catch((error) => {
          apiError(error);
        })
        .finally(() => {
          setSaving(false);
          onSaving(false);
        });
    },
    [apiError, dispatch, studentId, success, t, onSaving]
  );

  return (
    <AppForm formHook={formHook} onSubmit={handleSubmit} isDisabled={isSaving}>
      <AppForm.Group name="isOver18">
        <p>{t("my_profile.student.agreement.is_over_18")}</p>
        <AppForm.Radio
          name="isOver18"
          options={[
            { title: "common.yes", value: true },
            { title: "common.no", value: false },
          ]}
          rules={{ validate: (v) => (v === undefined ? "" : true) }}
        />
      </AppForm.Group>
      {isOver18 === false && (
        <>
          <AppForm.Group name="parentName">
            <AppForm.Input
              name="parentName"
              placeholder="my_profile.student.agreement.parent_name"
              rules={{
                required: true,
              }}
            />
          </AppForm.Group>
          <AppForm.Group name="parentSurname">
            <AppForm.Input
              name="parentSurname"
              placeholder="my_profile.student.agreement.parent_surname"
              rules={{
                required: true,
              }}
            />
          </AppForm.Group>
          <AppForm.Group name="parentPhone">
            <AppForm.Phone
              name="parentPhone"
              placeholder="my_profile.student.agreement.parent_phone"
              rules={{
                required: true,
              }}
            />
          </AppForm.Group>
        </>
      )}
      <AppForm.Group name="privacyLja">
        <AppForm.Checkbox
          name="privacyLja"
          rules={{ required: true }}
          label="my_profile.student.agreement.privacy_lja"
        />
        <Popup
          content={t("my_profile.student.agreement.privacy_lja_long")}
          style={{ whiteSpace: "pre-line" }}
          wide="very"
          basic
          trigger={
            <div className="popup-icon">
              <ShIcon name={"question"} />
            </div>
          }
        />
      </AppForm.Group>
      <AppForm.Group name="allowTravel">
        <AppForm.Checkbox
          name="allowTravel"
          rules={{ required: true }}
          label="my_profile.student.agreement.allow_travel"
        />
        <Popup
          content={t("my_profile.student.agreement.allow_travel_long")}
          style={{ whiteSpace: "pre-line" }}
          wide="very"
          basic
          trigger={
            <div className="popup-icon">
              <ShIcon name={"question"} />
            </div>
          }
        />
      </AppForm.Group>
      <AppForm.Group name="allowPhotos">
        <AppForm.Checkbox name="allowPhotos" label="my_profile.student.agreement.allow_photos" />
        <Popup
          content={
            <>
              {
                "Sutinku, kad LJA naudotų mano (arba mano atstovaujamo nepilnamečio) nuotraukas ir (ar) vaizdo įrašus su atvaizdu LJA Programos ir LJA veiklos viešinimui bei informacijos sklaidai spaudoje, LJA interneto svetainėje: "
              }{" "}
              <a target="_blank" href="https://lja.lt/" rel="noopener noreferrer">
                https://lja.lt/
              </a>
              {"; LJA Facebook paskyroje:"}{" "}
              <a target="_blank" href="https://www.facebook.com/JuniorAchievementLT" rel="noopener noreferrer">
                https://www.facebook.com/JuniorAchievementLT
              </a>{" "}
              {"ir LJA Instagram paskyroje:"}{" "}
              <a target="_blank" href="https://www.instagram.com/jaunieji_antrepreneriai" rel="noopener noreferrer">
                https://www.instagram.com/jaunieji_antrepreneriai
              </a>{" "}
              {
                ". Esu informuotas, kad pateikus rašytinį prašymą LJA, įskaitant elektronine forma, galiu bet kuriuo metu atšaukti savo sutikimą ir man dėl to nekils neigiamų pasekmių."
              }
            </>
          }
          style={{ whiteSpace: "pre-line" }}
          wide="very"
          basic
          trigger={
            <div className="popup-icon">
              <ShIcon name={"question"} />
            </div>
          }
        />
      </AppForm.Group>
      <AppForm.Group name="privacyPolicy">
        <AppForm.Checkbox
          name="privacyPolicy"
          label="my_profile.student.agreement.privacy_policy"
          rules={{ required: true }}
        />{" "}
        <Link to={NonAuthRoutes.privacyPolicy} target="_blank" rel="noopener noreferrer">
          {t("common.privacy_policy")}
        </Link>
        <Popup
          content={t("my_profile.student.agreement.privacy_policy_long")}
          style={{ whiteSpace: "pre-line" }}
          wide="very"
          basic
          trigger={
            <div className="popup-icon">
              <ShIcon name={"question"} />
            </div>
          }
        />
      </AppForm.Group>

      <Button primary loading={isSaving} disabled={isSaving} onClick={formHook.handleSubmit(handleSubmit)}>
        {t("my_profile.student.agreement.sign")}
      </Button>
    </AppForm>
  );
};

export default StudentProfileVirtualAgreementForm;
